import { TERRAIN } from '../core/SimConfig.js';

/**
 * CPU-side terrain height queries backed by the streamed heightmap cache.
 *
 * Provides three query modes:
 *   1. `getHeightAt(x, z)`       — single point, bilinear-interpolated
 *   2. `getHeightsAt(positions)`  — batch query, amortises tile lookups
 *   3. `raycast(ox, oy, oz, dx, dy, dz, maxDist)` — ray-heightfield intersection
 *
 * All methods return `null` when the required heightmap hasn't streamed in yet;
 * callers should fall back to groundY = 0 in that case.
 */

// Available heightmap resolutions, ordered best → worst.
// We always prefer the highest resolution that the cache has ready.
const HEIGHT_RESOLUTIONS = [...new Set(
  TERRAIN.lodRings.map((r) => r.textureRes),
)].sort((a, b) => b - a);

// Pre-computed grid extents (meters)
const { cols, rows, tileSize } = TERRAIN;
const [originX, originZ] = TERRAIN.origin;
const gridMinX = originX - (cols * tileSize) / 2;
const gridMinZ = originZ - (rows * tileSize) / 2;

export default class TerrainCollider {
  /**
   * @param {import('./TerrainTextureCache.js').default} cache — shared texture cache
   */
  constructor(cache) {
    this._cache = cache;
  }

  // ── Single-point query ────────────────────────────────────────────────

  /**
   * @param {number} worldX
   * @param {number} worldZ
   * @returns {number | null} terrain height at (worldX, worldZ), or null if
   *   the heightmap for that tile isn't loaded yet
   */
  getHeightAt(worldX, worldZ) {
    const col = Math.floor((worldX - gridMinX) / tileSize);
    const row = Math.floor((worldZ - gridMinZ) / tileSize);
    if (col < 0 || col >= cols || row < 0 || row >= rows) return null;

    const tileId = String.fromCharCode(65 + row) + (col + 1);
    const imageData = this._getBestHeightmap(tileId);
    if (!imageData) return null;

    // Local offset within tile (tile center is at the mesh origin)
    const centerX = gridMinX + (col + 0.5) * tileSize;
    const centerZ = gridMinZ + (row + 0.5) * tileSize;
    const localX = worldX - centerX;
    const localZ = worldZ - centerZ;

    return this._sampleBilinear(imageData, localX, localZ);
  }

  /**
   * Approximate the terrain surface normal at (worldX, worldZ) via central
   * differences of {@link getHeightAt}. Returns a unit {x, y, z} pointing up out
   * of the surface, or null if any sample's heightmap isn't loaded yet (callers
   * should fall back to world-up).
   *
   * @param {number} worldX
   * @param {number} worldZ
   * @param {number} [eps=2] — sample spacing in meters
   * @returns {{ x: number, y: number, z: number } | null}
   */
  getNormalAt(worldX, worldZ, eps = 2) {
    const hL = this.getHeightAt(worldX - eps, worldZ);
    const hR = this.getHeightAt(worldX + eps, worldZ);
    const hD = this.getHeightAt(worldX, worldZ - eps);
    const hU = this.getHeightAt(worldX, worldZ + eps);
    if (hL == null || hR == null || hD == null || hU == null) return null;

    // Normal = normalize(-dH/dx, 1, -dH/dz)
    const nx = -(hR - hL) / (2 * eps);
    const nz = -(hU - hD) / (2 * eps);
    const inv = 1 / Math.sqrt(nx * nx + 1 + nz * nz);
    return { x: nx * inv, y: inv, z: nz * inv };
  }

  // ── Batch query ───────────────────────────────────────────────────────

  /**
   * Query terrain height for many positions at once.
   * Groups by tile to avoid redundant cache lookups.
   *
   * @param {ArrayLike<{x: number, z: number}>} positions — world-space XZ
   * @returns {Float32Array} heights (NaN where heightmap unavailable)
   */
  getHeightsAt(positions) {
    const n = positions.length;
    const out = new Float32Array(n);

    // Group indices by tile key to fetch each heightmap once
    const groups = new Map(); // tileKey → { imageData, centerX, centerZ, indices[] }

    for (let i = 0; i < n; i++) {
      const { x, z } = positions[i];
      const col = Math.floor((x - gridMinX) / tileSize);
      const row = Math.floor((z - gridMinZ) / tileSize);

      if (col < 0 || col >= cols || row < 0 || row >= rows) {
        out[i] = NaN;
        continue;
      }

      const tileId = String.fromCharCode(65 + row) + (col + 1);
      const key = tileId; // one group per tile (best-res chosen once)

      let group = groups.get(key);
      if (!group) {
        const imageData = this._getBestHeightmap(tileId);
        group = {
          imageData,
          centerX: gridMinX + (col + 0.5) * tileSize,
          centerZ: gridMinZ + (row + 0.5) * tileSize,
          indices: [],
        };
        groups.set(key, group);
      }
      group.indices.push(i);
    }

    // Sample each group
    for (const { imageData, centerX, centerZ, indices } of groups.values()) {
      if (!imageData) {
        for (const i of indices) out[i] = NaN;
        continue;
      }
      for (const i of indices) {
        const localX = positions[i].x - centerX;
        const localZ = positions[i].z - centerZ;
        out[i] = this._sampleBilinear(imageData, localX, localZ);
      }
    }

    return out;
  }

  // ── Ray-heightfield intersection ──────────────────────────────────────

  /**
   * Cast a ray against the terrain heightfield.
   *
   * Uses a coarse fixed-step march followed by binary-search refinement.
   *
   * @param {number} ox  — ray origin X
   * @param {number} oy  — ray origin Y
   * @param {number} oz  — ray origin Z
   * @param {number} dx  — ray direction X (need not be normalised)
   * @param {number} dy  — ray direction Y
   * @param {number} dz  — ray direction Z
   * @param {number} maxDist — maximum ray length (meters)
   * @param {number} [stepSize=20] — coarse march step (meters)
   * @returns {{ x: number, y: number, z: number, distance: number } | null}
   */
  raycast(ox, oy, oz, dx, dy, dz, maxDist, stepSize = 20) {
    // Normalise direction
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 1e-10) return null;
    const invLen = 1 / len;
    dx *= invLen;
    dy *= invLen;
    dz *= invLen;

    // Quick rejection: ray pointing up and starting above max terrain height
    if (dy > 0 && oy > TERRAIN.heightBias + TERRAIN.heightScale) return null;

    // Coarse march
    let prevT = 0;
    let prevH = this.getHeightAt(ox, oz);
    let prevAbove = prevH === null ? (oy > 0) : (oy > prevH);

    const steps = Math.ceil(maxDist / stepSize);
    for (let i = 1; i <= steps; i++) {
      const t = Math.min(i * stepSize, maxDist);
      const px = ox + dx * t;
      const py = oy + dy * t;
      const pz = oz + dz * t;

      const h = this.getHeightAt(px, pz);
      if (h === null) {
        // No heightmap available for this tile — treat as flat 0
        const above = py > 0;
        if (!above && prevAbove) {
          // Crossed the y=0 fallback plane — approximate
          return this._bisectFallback(ox, oy, oz, dx, dy, dz, prevT, t);
        }
        prevT = t;
        prevAbove = above;
        continue;
      }

      const above = py > h;
      if (!above && prevAbove) {
        // Ray crossed below terrain between prevT and t — refine
        return this._bisect(ox, oy, oz, dx, dy, dz, prevT, t);
      }

      prevT = t;
      prevH = h;
      prevAbove = above;
    }

    return null;
  }

  // ── Line of sight ─────────────────────────────────────────────────────

  /**
   * Coarse line-of-sight test between two world points across the terrain.
   *
   * Ray-marches from A toward B (reusing {@link raycast}) and reports whether
   * the terrain occludes the segment. Tuned for speed over precision: pass a
   * large `stepSize` (e.g. ~300 m) for cheap "can this radar see that target"
   * queries where tunnelling through a thin ridge between samples is acceptable.
   *
   * A null heightmap (tile not yet streamed) is treated as non-occluding,
   * matching the rest of the collider's "fall back to clear" contract.
   *
   * @param {number} ax  — eye X
   * @param {number} ay  — eye Y
   * @param {number} az  — eye Z
   * @param {number} bx  — target X
   * @param {number} by  — target Y
   * @param {number} bz  — target Z
   * @param {number} [stepSize=305] — coarse march step (m); ~1000 ft default
   * @returns {boolean} true if A has a clear view of B (no terrain between)
   */
  hasLineOfSight(ax, ay, az, bx, by, bz, stepSize = 305) {
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 1e-6) return true;

    const hit = this.raycast(ax, ay, az, dx, dy, dz, dist, stepSize);
    if (!hit) return true;

    // The endpoint (B) sits above the ground, so the ray never registers a hit
    // exactly at `dist`; any crossing short of it is genuine terrain in the way.
    // The 2 m epsilon only guards against float noise at the far end.
    return hit.distance >= dist - 2;
  }

  // ── Internals ─────────────────────────────────────────────────────────

  /**
   * Fetch the highest-resolution heightmap currently cached for this tile.
   * @returns {ImageData | undefined}
   */
  _getBestHeightmap(tileId) {
    for (const res of HEIGHT_RESOLUTIONS) {
      const img = this._cache.getHeight(tileId, res);
      if (img) return img;
    }
    return undefined;
  }

  /**
   * Bilinear-interpolated height sample from an ImageData heightmap.
   * @param {ImageData} imageData
   * @param {number} localX — metres from tile center (+X = east)
   * @param {number} localZ — metres from tile center (+Z = south)
   * @returns {number} height in metres
   */
  _sampleBilinear(imageData, localX, localZ) {
    const { width, height, data } = imageData;
    const half = tileSize / 2;

    // Local coords → continuous pixel coords (matching _displaceVertices UV convention)
    const u = (localX + half) / tileSize;           // 0 = west, 1 = east
    const v = 1 - (half - localZ) / tileSize;       // 0 = north (row 0), 1 = south

    const fx = u * (width - 1);
    const fy = v * (height - 1);

    const x0 = Math.max(0, Math.min(width - 2, Math.floor(fx)));
    const y0 = Math.max(0, Math.min(height - 2, Math.floor(fy)));
    const x1 = x0 + 1;
    const y1 = y0 + 1;

    const sx = fx - x0;
    const sy = fy - y0;

    // Red channel of each corner pixel
    const r00 = data[(y0 * width + x0) * 4];
    const r10 = data[(y0 * width + x1) * 4];
    const r01 = data[(y1 * width + x0) * 4];
    const r11 = data[(y1 * width + x1) * 4];

    const lum = (r00 * (1 - sx) * (1 - sy)
               + r10 * sx * (1 - sy)
               + r01 * (1 - sx) * sy
               + r11 * sx * sy) / 255;

    return TERRAIN.heightBias + lum * TERRAIN.heightScale;
  }

  /**
   * Binary-search refinement between two ray parameters where a terrain
   * crossing was detected.
   */
  _bisect(ox, oy, oz, dx, dy, dz, tLo, tHi) {
    for (let i = 0; i < 8; i++) {
      const tMid = (tLo + tHi) * 0.5;
      const px = ox + dx * tMid;
      const py = oy + dy * tMid;
      const pz = oz + dz * tMid;
      const h = this.getHeightAt(px, pz);
      if (h === null || py > h) {
        tLo = tMid;
      } else {
        tHi = tMid;
      }
    }
    const t = (tLo + tHi) * 0.5;
    return {
      x: ox + dx * t,
      y: oy + dy * t,
      z: oz + dz * t,
      distance: t,
    };
  }

  /** Bisect against the fallback y=0 plane. */
  _bisectFallback(ox, oy, oz, dx, dy, dz, tLo, tHi) {
    // Analytical plane intersection: y = oy + dy*t = 0 → t = -oy/dy
    if (Math.abs(dy) > 1e-10) {
      const t = -oy / dy;
      if (t >= tLo && t <= tHi) {
        return {
          x: ox + dx * t,
          y: 0,
          z: oz + dz * t,
          distance: t,
        };
      }
    }
    // Fallback to midpoint
    const t = (tLo + tHi) * 0.5;
    return {
      x: ox + dx * t,
      y: 0,
      z: oz + dz * t,
      distance: t,
    };
  }
}
